import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowLeft, Calendar } from "lucide-react";
import { format } from "date-fns";
import { BlogCard } from "../components/BlogCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (slug) {
          // Fetch single post
          const response = await axios.get(`${API}/blog/${slug}`);
          setCurrentPost(response.data);
        } else {
          // Fetch all posts
          const response = await axios.get(`${API}/blog`);
          setPosts(response.data);
        }
      } catch (error) {
        console.error("Error fetching blog:", error);
        if (slug) {
          navigate("/blog");
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [slug, navigate]);

  // Single post view
  if (slug && currentPost) {
    return (
      <div data-testid="blog-post-page">
        {/* Hero */}
        <section className="relative py-32 bg-[#0A0A0A] overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <img
              src={currentPost.image_url}
              alt={currentPost.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/60" />
          <div className="relative z-10 max-w-4xl mx-auto px-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#7ED4C6] mb-8 hover:text-[#D4B03A] transition-colors"
              data-testid="back-to-blog"
            >
              <ArrowLeft size={18} />
              Back to Blog
            </Link>
            <div className="flex items-center gap-4 text-[#A1A1AA] text-sm mb-4">
              <span className="flex items-center gap-2">
                <Calendar size={14} />
                {currentPost.created_at && format(new Date(currentPost.created_at), "MMMM dd, yyyy")}
              </span>
              <span>By {currentPost.author}</span>
            </div>
            <h1 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mb-4">
              {currentPost.title}
            </h1>
            <div className="section-divider" />
          </div>
        </section>

        {/* Content */}
        <section className="py-16 bg-[#121212]">
          <div className="max-w-3xl mx-auto px-6">
            <div
              className="prose prose-lg prose-invert max-w-none
                prose-headings:font-['Oswald'] prose-headings:text-white
                prose-p:text-[#A1A1AA] prose-p:leading-relaxed
                prose-a:text-[#7ED4C6] prose-a:no-underline hover:prose-a:underline
                prose-strong:text-white
                prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3"
              dangerouslySetInnerHTML={{ __html: currentPost.content }}
            />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#0A0A0A] border-t border-[#2A2A2A]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-['Oswald'] text-3xl font-bold text-white mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-[#A1A1AA] mb-8">
              Contact us today for a free consultation and estimate.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
              Request a Quote
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </div>
    );
  }

  // Blog listing view
  return (
    <div data-testid="blog-page">
      {/* Hero Section */}
      <section className="relative py-32 bg-[#0A0A0A] overflow-hidden" data-testid="blog-hero">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920"
            alt="Blog"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
            Our Blog
          </span>
          <h1 className="font-['Oswald'] text-5xl md:text-6xl font-bold text-white mb-6">
            Construction Insights
          </h1>
          <div className="section-divider" />
          <p className="text-[#A1A1AA] text-xl max-w-2xl mt-6">
            Expert tips, industry insights, and updates from the Astute Builders team.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-24 bg-[#121212]" data-testid="blog-grid">
        <div className="max-w-7xl mx-auto px-6">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-[#1E1E1E] animate-pulse">
                  <div className="aspect-video bg-[#2A2A2A]" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-[#2A2A2A] w-1/2" />
                    <div className="h-6 bg-[#2A2A2A] w-full" />
                    <div className="h-4 bg-[#2A2A2A] w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#A1A1AA] text-lg">No blog posts found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="subscribe-section">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
            Stay Updated
          </span>
          <h2 className="font-['Oswald'] text-4xl font-bold text-white mb-4">
            Get Construction Tips & Updates
          </h2>
          <div className="section-divider mx-auto" />
          <p className="text-[#A1A1AA] mt-6 mb-8">
            Subscribe to receive the latest news, tips, and insights from our team.
          </p>
          <form className="flex flex-col sm:flex-row gap-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="form-input flex-1"
              data-testid="subscribe-email"
            />
            <button type="submit" className="btn-primary" data-testid="subscribe-btn">
              Subscribe
            </button>
          </form>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#7ED4C6]" data-testid="blog-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-6">
            Have Questions About Your Project?
          </h2>
          <p className="text-[#0A0A0A]/80 text-lg mb-8">
            Our team is ready to help. Contact us for expert advice and a free consultation.
          </p>
          <Link
            to="/contact"
            className="bg-[#0A0A0A] text-white font-['Oswald'] font-semibold uppercase tracking-wider px-8 py-4 hover:bg-[#121212] transition-colors inline-flex items-center justify-center gap-2"
            data-testid="blog-contact-btn"
          >
            Contact Us
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
